import React, { FC } from 'react'
import DocumentTitle from 'react-document-title'
import { Route } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import NavigationBar from 'transfix-ui/components/NavigationBar'
import { GlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { QueryParamProvider } from 'use-query-params'
import { getApiUrl } from 'utils/api/apiHelpers'
import { BreakpointProvider } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { marketplaceClient, marketplaceBatchClient } from 'utils/graphql/client'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { RogersRestfulProvider } from 'components/RogersRestfulProvider'
import { Router } from 'components/Router'
import { Footer } from 'components/layout'
import { generatePageTitle } from 'utils/generateAppDefaults'
import { ConnectedPhoneNumberProvider } from './ConnectedPhoneNumber'

export enum AppWrapNavVariants {
  side = 'side',
  top = 'top',
  none = 'none',
}

interface IAppWrapProps {
  appName: string
  title?: string
  noFooter?: boolean
  noHeader?: boolean
  variant?: AppWrapNavVariants
}

export const AppWrap: FC<IAppWrapProps> = ({
  appName = '',
  variant = AppWrapNavVariants.top,
  children,
  title,
  noFooter,
  noHeader,
}) => {
  const { logout } = useUserDetails()
  const isGraphqlBatchingEnabled = useFeatureFlag('graphqlBatching')
  const apolloClient = isGraphqlBatchingEnabled ? marketplaceBatchClient : marketplaceClient

  return (
    <ApolloProvider client={apolloClient}>
      <BreakpointProvider>
        <DocumentTitle title={title || generatePageTitle(appName)}>
          <Router basename={appName}>
            <QueryParamProvider ReactRouterRoute={Route}>
              <RogersRestfulProvider
                baseApi={getApiUrl('MARKETPLACE')}
                onUnauthenticatedResponse={logout}>
                <ConnectedPhoneNumberProvider>
                  <ErrorBoundary>
                    <GlobalToast />
                    {variant === AppWrapNavVariants.top && (
                      <>
                        {!noHeader && <NavigationBar />}
                        <section className='container-fluid' data-test={appName}>
                          {children}
                        </section>
                        {!noFooter && <Footer />}
                      </>
                    )}
                    {variant === AppWrapNavVariants.none && (
                      <div className='flex without-sidenav'>
                        <main className='ma0 pa0 flex flex-column'>{children}</main>
                      </div>
                    )}
                  </ErrorBoundary>
                </ConnectedPhoneNumberProvider>
              </RogersRestfulProvider>
            </QueryParamProvider>
          </Router>
        </DocumentTitle>
      </BreakpointProvider>
    </ApolloProvider>
  )
}
